import apiClient from './client';

/**
 * POST /sales
 * Body: { customer_id, line_items: [{product_id, batch_id, quantity, unit_price}],
 *         shipping_amount?, notes?, ... }
 * Returns the created sale (status defaults to draft).
 */
export async function createSale(payload) {
  const response = await apiClient.post('/sales', payload);
  return response.data;
}

/**
 * GET /sales/{id}
 * Returns the full SaleOut — line items, totals, status, PDF URLs, etc.
 */
export async function getSale(saleId) {
  const response = await apiClient.get(`/sales/${saleId}`);
  return response.data;
}

/**
 * GET /sales
 * Paginated list. Backend supports filters: status, customer_id, search,
 * outstanding_only, date_from, date_to. PWA only exposes status + search
 * for now to keep the mobile UI simple — desktop covers the rest.
 *
 * Response: { items, total, page, page_size, pages }
 */
export async function listSales({
  page = 1,
  pageSize = 20,
  status,
  search,
} = {}) {
  const params = { page, page_size: pageSize };
  if (status) params.status = status;
  if (search) params.search = search;
  const response = await apiClient.get('/sales', { params });
  return response.data;
}

/**
 * GET /sales/{id}/pdf?type=proforma | invoice | release_note
 *
 * The proforma is generated on demand — Sale.proforma_pdf_url is null for
 * fresh sales, so the only reliable way to view a proforma is to call this
 * endpoint and stream the bytes back. Endpoint requires auth, so we fetch
 * via apiClient (which attaches the Bearer token) as a blob, then the
 * caller can `URL.createObjectURL()` it for viewing.
 */
export async function getSalePdfBlob(saleId, type = 'proforma') {
  const response = await apiClient.get(`/sales/${saleId}/pdf`, {
    params: { type },
    responseType: 'blob',
  });
  return response.data; // Blob
}

/**
 * PATCH /sales/{id}
 *
 * Body: any subset of { customer_id, date, delivery_date, notes,
 *   shipping_amount, shipping_cost, shipping_description, line_items }.
 * PWA only sends `line_items` for now (Simon's scope: line items only on
 * mobile; customer/notes/dates stay locked).
 *
 * line_items shape (per item): { product_id, batch_id, quantity, unit_price }
 *   — same as createSale. Backend releases the old line items' stock,
 *   re-reserves stock for the new items via FIFO, then recomputes
 *   subtotal / VAT / total / outstanding_amount in one go.
 *
 * Backend preconditions:
 *   - sale.status === "draft" (400 otherwise — message: "Only draft
 *     sales can be edited")
 *   - require_admin_or_sales (sales reps pass)
 *
 * Returns the full updated SaleOut (same shape as GET /sales/{id}); drop
 * straight into local state.
 */
export async function updateSale(saleId, payload) {
  const response = await apiClient.patch(`/sales/${saleId}`, payload);
  return response.data; // SaleOut
}

/**
 * DELETE /sales/{id}
 *
 * Hard-deletes a draft sale. Backend (`require_admin_or_sales`) releases
 * any reserved on-hold stock back to available, then cascade-deletes
 * line items + expected payments. 204 No Content on success.
 *
 * Backend preconditions:
 *   - sale.status === "draft" (400 otherwise — "Use revert or cancel
 *     for delivered/paid sales.")
 *
 * Returns nothing (axios still resolves so callers can chain).
 */
export async function deleteSale(saleId) {
  await apiClient.delete(`/sales/${saleId}`);
}

/**
 * POST /sales/{id}/revert
 *
 * Reverts a delivered or paid sale. Two flavours:
 *   - action='revert_to_draft' → status=draft, stock sold→on_hold,
 *     payments detached as customer credit, ledger reversed,
 *     fiscal_number + delivery_date cleared. Sale becomes editable
 *     again.
 *   - action='cancel' → status=cancelled, stock sold→available,
 *     payments detached as customer credit, ledger reversed.
 *     Permanent.
 *
 * Body: { action, reason: string, notes?: string }
 *   - reason is required; backend treats it as free text but desktop UI
 *     restricts the choice to a fixed list (Pricing error / Customer
 *     cancellation / Wrong items delivered / Duplicate entry / Other).
 *
 * Backend preconditions:
 *   - sale.status in ("delivered", "paid")
 *   - sale.fiscal_number is null (fiscalised sales must use Credit
 *     Note instead — backend returns 400 with that message)
 *
 * AUTH GOTCHA: handler uses `require_admin`, NOT `require_admin_or_sales`.
 * Sales reps will get 403. If reps need this on mobile, relax the
 * dependency to `require_admin_or_sales` to match the delete row above.
 *
 * Returns the full updated SaleOut so the page re-renders against the
 * new status without a refetch.
 */
export async function revertSale(saleId, { action, reason, notes }) {
  const body = { action, reason };
  if (notes != null && notes !== '') body.notes = notes;
  const response = await apiClient.post(`/sales/${saleId}/revert`, body);
  return response.data; // SaleOut
}

/**
 * POST /sales/{id}/deliver
 *
 * No body. Backend (`require_admin_or_sales`) marks status=delivered,
 * sets delivery_date if missing, deducts stock FIFO (on_hold → sold via
 * confirm_stock_for_items), and creates the customer ledger debit
 * (record_invoice). Fiscalisation does NOT happen here — that's a
 * separate desktop+2FA endpoint.
 *
 * Precondition: sale.status === "draft". Anything else returns 400.
 *
 * Returns the full updated SaleOut so callers can drop it into local
 * state and the page re-renders against the new status / delivery_date /
 * outstanding_amount.
 */
export async function deliverSale(saleId) {
  const response = await apiClient.post(`/sales/${saleId}/deliver`);
  return response.data; // SaleOut
}
